<script>
import debounce from 'lodash/debounce';
import { v1 as uuid } from "uuid";

export default {
  name: 'TabsCarousel',
  props: {
    carouselData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      clientWidth: null,
      clientHeight: null,
      calculateAspectRatio: true,
      slides: null,
      swiper: null,
      sectionRefId: uuid(),
    };
  },
  mounted() {
    this.initCarouselData();
    this.loadSwiperScript();
    window.addEventListener('resize', debounce(this.calculateThumbsHeight, 300));
  },

  methods: {
    initCarouselData() {
      if (this.carouselData) {
        this.slides = this.carouselData.slides.map((slideData, index) => ({
          active: index === 0,
          prevActive: null,
          ...slideData,
        }));
      }
    },
    
    loadSwiperScript() { // Load Swiper script
      const swiperScript = document.createElement("script");
      swiperScript.src = "https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js";
      swiperScript.async = true;
      document.head.appendChild(swiperScript);
      swiperScript.onload = this.initSwiper;
    },

    initSwiper() { // Initialize Swiper
      this.swiperThumbs();
      this.swiperSlides();
      this.calculateThumbsHeight();
    },

    swiperThumbs() {
      //Thumbs
      this.swiperThumbs = new Swiper(".SwiperThumbs", {
        allowTouchMove: false,
        slidesPerView: "auto", //slidesPerView: this.slidesCount, Using auto prevent slider trying to calculate width
        watchSlidesProgress: true,
        WatchSlidesVisibility: true,

        on: { //fix for mac and magic mouse issues
          touchEnd: function(s,e) {
            let range = 5;
            let diff = s.touches.diff = s.isHorizontal() ? s.touches.currentX - s.touches.startX : s.touches.currentY 
                        - s.touches.startY
            if (diff < range || diff > -range) s.allowClick = true;
          }
        },
      });
    },
    swiperSlides() {
      // Swiper
      this.swiper = new Swiper(".mySwiper", {
        loop: true,
        
        autoplay: {
          delay: 5500,
          disableOnInteraction: true,
        },
        pagination: {
          el: ".swiper-pagination",
          clickable: true,
        },
        navigation: {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
        thumbs: {
          swiper: this.swiperThumbs, // reference for the thumbs swiper
        },
      });
    },

    calculateThumbsHeight() {
      
      this.thumbHeight = 0; // Initialize with a default value
      const thumbNav = document.querySelector('.SwiperThumbs');
      if (thumbNav) {
        this.thumbHeight = thumbNav.offsetHeight;
        // console.log('thumbNav height:', this.thumbHeight);
      } else {
        console.error('thumbNav not found');
      }

      this.sliderHeight = 0; // Initialize with a default value
      const slideSize = document.querySelector('.mySwiper');
      if (slideSize) {
        this.sliderHeight = slideSize.offsetHeight;
        // console.log('slideSize height:', this.sliderHeight);
      } else {
        console.error('slideSize not found');
      }
      
      this.$refs[this.sectionRefId].style.setProperty('--swiper-tab-height', this.thumbHeight + 'px');
      this.$refs[this.sectionRefId].style.setProperty('--swiper-tab-height-negative', '-' + this.thumbHeight + 'px');
      this.$refs[this.sectionRefId].style.setProperty('--swiper-slider-height', this.sliderHeight + 'px');
    },
  },
};
</script>

<template>
<section
  :aria-label="isMobile ? false : carouselData.sectionLabel"
  :aria-roledescription="isMobile ? false : 'carousel'"
  class="section tabs-carousel-swiper theme--white"
  :ref="sectionRefId"
>
  <div class="tabs-carousel-swiper__container-mobile">
    <div
      :id="`tab-carousel-slide-${slide.id}`"
      v-for="(slide, index) in slides"
      :key="index"
      class="tabs-carousel-swiper__tab"
    >
      <div class="tabs-carousel-swiper__title">
        {{ slide.title }}
      </div>
      <div
        class="tabs-carousel-swiper__content-box"
        :id="`tab-carousel-panel-${slide.id}`"
      >
        <!-- @molecule-background -->
        <div class="background background--size-contain">
          <div class="media media--picture media--size-contain">
            <picture
              v-if="slide.background"
              class="picture picture--size-contain"
            >
              <!-- Large Mobile (576px) - 767x1056 -->
              <source
                v-if="slide.background.largeMobileSrc"
                :srcset="slide.background.largeMobileSrc"
                :media="`(min-width: 576px)`"
              >

              <!-- Default Mobile - 575x788 -->
              <img
                :src="slide.background.src"
                :alt="slide.background.alt"
              >
            </picture>
          </div>
          <div class="background__dimmer" />
        </div><!-- /@molecule-background -->
        <div class="tabs-carousel-swiper__content-container">
          <div class="tabs-carousel-swiper__description">
            <!-- @atom-wysiwyg -->
            <div
              class="wysiwyg"
              v-html="slide.description"
            />
          </div>
          <div
            v-if="slide.button"
            class="tabs-carousel-swiper__cta"
          >
            <!-- @atom-button--yellow -->
            <a
              :href="slide.button.href"
              class="btn"
              :class="[slide.button.style ? `btn--${slide.button.style}` : '']"
              :target="slide.button.target ? slide.button.target : null"
            >
              <span class="btn__content">
                <span class="btn__text">{{ slide.button.text }}</span>
              </span>
            </a><!-- /@atom-button--yellow -->
          </div><!-- tabs-carousel-swiper__cta -->
        </div><!-- tabs-carousel-swiper__content-container -->
      </div><!-- /tabs-carousel-swiper__content-box -->
    </div><!-- /tabs-carousel-swiper__tab -->
  </div><!-- /tabs-carousel-swiper__container-mobile -->

  <div class="tabs-carousel-swiper__container">
    <div class="swiper mySwiper">
      <div class="swiper-wrapper">
        <div
          class="swiper-slide"
          v-for="(slide, index) in slides"
          :key="index"
        >

          <!-- @molecule-background -->
          <div
            class="background background--size-contain"
            :class="{ 'background--active': slide.active, 'background--prevactive': slide.prevActive }"
          >
            <div class="media media--picture media--size-contain">
              <picture
                v-if="slide.background"
                class="picture picture--size-contain"
              >
                <!-- Desktop (992px) - 1920x972 -->
                <source
                  v-if="slide.background.desktopSrc"
                  :srcset="slide.background.desktopSrc"
                  :media="`(min-width: 992px)`"
                >

                <!-- Tablet (768px) - 991x773 -->
                <source
                  v-if="slide.background.tabletSrc"
                  :srcset="slide.background.tabletSrc"
                  :media="`(min-width: 768px)`"
                >

                <!-- Large Mobile (576px) - 767x1056 -->
                <source
                  v-if="slide.background.largeMobileSrc"
                  :srcset="slide.background.largeMobileSrc"
                  :media="`(min-width: 576px)`"
                >

                <!-- Default Mobile - 575x788 -->
                <img
                  :src="slide.background.src"
                  :alt="slide.background.alt"
                >
              </picture>
            </div>
            <div class="background__dimmer" />
          </div><!-- /@molecule-background -->

          <div class="tabs-carousel-swiper__content-container">
            <div class="tabs-carousel-swiper__content">
              <div class="tabs-carousel-swiper__description">
                <div
                  class="wysiwyg"
                  v-html="slide.description"
                />
                <div
                  v-if="slide.button"
                  class="tabs-carousel-swiper__cta"
                >
                  <a
                    :href="slide.button.href"
                    class="btn"
                    :class="[slide.button.style ? `btn--${slide.button.style}` : '']"
                    :target="slide.button.target ? slide.button.target : null"
                  >
                    <span class="btn__content">
                      <span class="btn__text">{{ slide.button.text }}</span>
                    </span>
                  </a>
                </div> <!-- tabs-carousel-swiper__cta -->
              </div> <!-- tabs-carousel-swiper__description -->
            </div> <!-- tabs-carousel-swiper__content -->
          </div> <!-- tabs-carousel-swiper__content-container -->
        </div> <!-- swiper-slide -->

      </div> <!-- swiper-wrapper -->
      <div class="swiper-button-next"></div>
      <div class="swiper-button-prev"></div>
      <div class="swiper-pagination"></div>
    </div><!-- Swiper slider -->

    <div thumbsSlider="" class="swiper SwiperThumbs" ref="swiperThumbs">
      <div class="swiper-wrapper tabs-carousel-swiper__wrapper">
        <div
          v-for="(slide, index) in slides"
          :key="[index + ' thumb']" 
          class="swiper-slide tabs-carousel-swiper__thumb"
        >
          <div class="tabs-carousel-swiper__title">
            {{ slide.title }}
          </div>
        </div>
      </div> <!-- swiper-wrapper thumb -->
    </div><!-- swiper thumbs -->
  </div>
</section>
</template>
